import messageHandler from './MessageHandler';
import { getToken } from '@/utils/auth'
class WebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.baseUrl = process.env.VUE_APP_BASE_WS_URL  + process.env.VUE_APP_BASE_API;
    // 自动重连配置：5秒后重连
    this.reconnectInterval = 5000;
    this.reconnectTimer = null;
    // 订阅管理：暂存主题，重连后自动补订
    this.subscriptions = new Set();
   // 已确认订阅的主题集合  用来判断是否订阅成功
    this.confirmedTopics = new Set();
    //消息分发：按主题路由回调（发布/订阅模式核心）
    this.messageCallbacks = new Map(); // 按主题存储的回调函数
    this.globalCallbacks = []; // 全局消息回调
    // 连接状态回调：连接成功、断开、错误等
    this.connectionCallbacks = [];
    this.disconnectionCallbacks = [];
    this.errorCallbacks = [];
    // 认证失败回调：独立处理，避免死循环重连
    this.authFailureCallbacks = [];
    // 订阅确认回调：后端回 SUBSCRIBE_CONFIRM 时触发
    this.subscribeConfirmCallbacks = [];

    this.username = null;
    // 心跳定时器：防止连接空闲断开
    this.heartbeatTimer = null;
  }

  /**
   * 初始化WebSocket连接（带Token认证）
   * @param {string} token - 认证Token
   */
  init() {
    this.disconnect();
    const token = getToken()
    if (!token) {
      this.triggerAuthFailure('没有找到有效的认证Token，无法重连');
      return;
    }

    try {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const base = this.baseUrl
      const url = `${wsProtocol}${base}/ws/notification?token=${encodeURIComponent(token)}`;
      this.socket = new WebSocket(url);

      this.socket.onopen = (event) => this.handleOpen(event);
      this.socket.onmessage = (event) => this.handleMessage(event);
      this.socket.onclose = (event) => this.handleClose(event);
      this.socket.onerror = (error) => this.handleError(error);
    } catch (error) {
      console.error('WebSocket初始化失败', error);
      this.errorCallbacks.forEach(callback => callback(error));
      this.scheduleReconnect();
    }
  }

  /**
   * 处理连接打开事件
   */
  handleOpen(event) {
    console.log('WebSocket连接成功');
    this.isConnected = true;
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    // 启动心跳检测
    this.startHeartbeat();
  }

  /**
   * 处理接收到的消息
   */
  handleMessage(event) {
    try {
      const message = JSON.parse(event.data);
      // 处理连接成功消息
      if (message.type === 'CONNECTED') {
        // 通知所有监听者连接成功消息
        this.connectionCallbacks.forEach(callback => callback(message));
        // 重订阅所有主题
        this.resubscribe();
        return;
      }

      // 处理订阅确认消息（协议层，不进入业务分发）
      if (message.type === 'SUBSCRIBE_CONFIRM') {
        const topic = message.destination;
        if (topic) {
          this.confirmedTopics.add(topic);
        }
        console.log('订阅确认:', topic);
        this.subscribeConfirmCallbacks.forEach(callback => callback(message));
        return;
      }

      // 处理取消订阅确认消息
      if (message.type === 'UNSUBSCRIBE_CONFIRM') {
        const topic = message.destination;
        if (topic) {
          this.confirmedTopics.delete(topic);
        }
        console.log('取消订阅确认:', topic);
        return;
      }
      
      // 使用消息处理器处理消息
      const processedMessage = messageHandler.handleMessage(message);
      
      // 触发全局回调
      this.globalCallbacks.forEach(callback => 
        callback(processedMessage, message)
      );
      
      // 如果有目标主题，触发对应主题的回调
      if (message.destination) {
        const callbacks = this.messageCallbacks.get(message.destination) || [];
        callbacks.forEach(callback => 
          callback(processedMessage, message)
        );
      }
      
    } catch (error) {
      console.error('解析消息失败:', error, '原始数据:', event.data);
      this.errorCallbacks.forEach(callback => callback({
        type: 'PARSE_ERROR',
        message: '消息解析失败',
        originalData: event.data,
        error: error.message
      }));
    }
  }

  /**
   * 处理连接关闭事件
   */
  handleClose(event) {
    console.log('WebSocket连接关闭', event);
    this.isConnected = false;
    this.stopHeartbeat();
    
    this.disconnectionCallbacks.forEach(callback => callback(event));
    
    // 认证失败
    if (event.code === 1008) {
      this.triggerAuthFailure(event.reason || 'WebSocket认证失败');
      return;
    }
    
    // 自动重连
    if (this.socket) {
      this.scheduleReconnect();
    }
  }

  /**
   * 处理错误事件
   */
  handleError(error) {
    console.error('WebSocket错误', error);
    this.errorCallbacks.forEach(callback => callback(error));
  }

  /**
   * 启动心跳检测
   */
  startHeartbeat() {
    this.stopHeartbeat();
    // 每30秒发送一次心跳
    this.heartbeatTimer = setInterval(() => {
      if (this.isConnected) {
        this.send({
          type: 'HEARTBEAT',
          timestamp: Date.now()
        });
      }
    }, 30000);
  }

  /**
   * 停止心跳检测
   */
  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * 订阅主题 返回取消主体
   * 收到消息: { type: 'MESSAGE', destination: '/topic/orders' }
   * → callback1() 执行（订单列表页面：刷新列表）                
│  * → callback2() 执行（消息通知组件：显示红点）                
│  * → callback3() 执行（声音提示组件：播放提示音） 
   * 每个回调只做一件事，符合单一职责原则： 刷新列表  显示红点  播放提示音  一个主题要做多件事情 
   * @param {string} topic - 订阅主题
   * @param {function} callback - 消息回调函数
   * @param {object} options - 选项，如消息类型过滤
   */
  subscribe(topic, callback, options = {}) {
    //没有数据就初始化一个空数组
    if (!this.messageCallbacks.has(topic)) {
      this.messageCallbacks.set(topic, []);
    }
    // 包装回调函数，支持消息类型过滤
    const wrappedCallback = (processedMessage, originalMessage) => {
      if (options.messageTypes && !options.messageTypes.includes(originalMessage.type)) {
        return; // 过滤掉不需要的消息类型
      }
      callback(processedMessage, originalMessage);
    };
    //map  key []  所有订阅了这个主题的回调函数
    this.messageCallbacks.get(topic).push(wrappedCallback);
    
    if (this.isConnected) {
      this.sendSubscribeRequest(topic);
    } else {
      // 连接未建立时，将订阅请求加入队列
      this.subscriptions.add(topic);
      console.log(`将在连接成功后订阅主题: ${topic}`);
    }
    
    // 返回取消订阅函数
    return () => this.unsubscribe(topic, wrappedCallback);
  }

  /**
   * 发送订阅请求
   */
  sendSubscribeRequest(topic) {
    this.send({
      type: 'SUBSCRIBE',
      destination: topic,
      timestamp: Date.now()
    });
  }

  /**
   * 取消订阅
   */
  unsubscribe(topic, callback) {
    if (this.messageCallbacks.has(topic)) {
      const callbacks = this.messageCallbacks.get(topic);
      if (callback) {
        const index = callbacks.indexOf(callback);
        if (index !== -1) {
          callbacks.splice(index, 1);
        }
      } else {
        this.messageCallbacks.set(topic, []);
      }
      
      if (callbacks.length === 0) {
        this.messageCallbacks.delete(topic);
        this.sendUnsubscribeRequest(topic);
      }
    }
    
    this.subscriptions.delete(topic);
  }

  /**
   * 发送取消订阅请求
   */
  sendUnsubscribeRequest(topic) {
    if (this.isConnected) {
      this.send({
        type: 'UNSUBSCRIBE',
        destination: topic,
        timestamp: Date.now()
      });
    }
  }

  /**
   * 发送消息到指定主题
   * 暂时没有用 不要发送信息
   * @param {string} topic - 目标主题
   * @param {object} payload - 消息负载
   */
  sendToTopic(topic, payload) {
    return this.send({
      type: 'MESSAGE',
      destination: topic,
      payload: payload,
      timestamp: Date.now()
    });
  }

  /**
   * 发送原始消息
   */
  send(message) {
    if (this.isConnected && this.socket) {
      try {
        this.socket.send(JSON.stringify(message));
        return true;
      } catch (error) {
        console.error('发送消息失败:', error);
        this.errorCallbacks.forEach(callback => callback(error));
        return false;
      }
    } else {
      console.warn('WebSocket未连接，无法发送消息');
      return false;
    }
  }

  /**
   * 重新订阅所有主题
   */
  resubscribe() {
    if (this.isConnected && this.subscriptions.size > 0) {
      console.log('重新订阅所有主题...');
      this.subscriptions.forEach(topic => {
        this.sendSubscribeRequest(topic);
      });
    }
  }

  /**
   * 安排重连
   */
  scheduleReconnect() {
    if (!this.reconnectTimer) {
      console.log(`将在${this.reconnectInterval/1000}秒后尝试重连...`);
      this.reconnectTimer = setTimeout(() => {
        console.log('尝试重连WebSocket...');
        this.init();
      }, this.reconnectInterval);
    }
  }

  /**
   * 断开连接
   */
  disconnect() {
    if (this.socket) {
      this.socket.close(1000, '正常关闭');
      this.socket = null;
    }
    
    this.isConnected = false;
    this.username = null;
    this.stopHeartbeat();
    this.confirmedTopics.clear();
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * 触发认证失败回调
   */
  triggerAuthFailure(reason) {
    console.error('WebSocket认证失败:', reason);
    this.authFailureCallbacks.forEach(callback => callback(reason));
  }

  // 回调注册方法 将返回的消息交由调用方处理  多个组件可以注册不同的回调函数
  onConnect(callback) { this.connectionCallbacks.push(callback); }
  onDisconnect(callback) { this.disconnectionCallbacks.push(callback); }
  onError(callback) { this.errorCallbacks.push(callback); }
  onAuthFailure(callback) { this.authFailureCallbacks.push(callback); }
  // 全局消息回调
  onGlobalMessage(callback) { this.globalCallbacks.push(callback); }
  // 订阅确认回调：后端回 SUBSCRIBE_CONFIRM 时触发，callback 收到原始 message
  onSubscribeConfirm(callback) { this.subscribeConfirmCallbacks.push(callback); }
}

export default new WebSocketService();
